import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useIndexDateEdit } from '@verantos-dev/app-state-cohort-builder';
import {
  ApiWorkflowDialog,
  ComboBox,
  type DropdownListItem,
  Dropdown,
  DatePicker,
} from '@verantos-dev/shared-components';
import { IndexDateTypeLegacy } from '@verantos-dev/types-cohorts';

import IndexDateRangeSection from './IndexDateRangeSection';
import { useEmitResearchAssistantUpdate } from '../../../../hooks/common/useResearchAssistantUpdateForStudyPeriodCard';

export const IndexDateEditCard = () => {
  const intl = useIntl();
  const theme = useTheme();

  const {
    isSaveAllowed,
    indexDateId,
    indexDateType,
    indexDateValue,
    indexDateValueAsDate,
    inputFieldConfigMap,
    onClickCancelIndexDate,
    onClickModalSetIndexDate,
    onEventChange,
    onDatePickerChange,
    onDatePickerError,
    datePickerErrorMsg,
    onSelectTypeChange,
    indexStartDatePicker,
    indexEndDatePicker,
    isDateRangeEnabled,
    isEditable,
    onClickDateRangeCheckbox,
  } = useIndexDateEdit(dayjs('1900-01-01'), dayjs('2099-12-31'));
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();

  const indexDateDropdownOptions: DropdownListItem[] = [
    { value: IndexDateTypeLegacy.CALENDAR, displayValue: IndexDateTypeLegacy.CALENDAR },
    { value: IndexDateTypeLegacy.EVENT, displayValue: IndexDateTypeLegacy.EVENT },
  ];

  const indexDateValueInputFieldConfig = inputFieldConfigMap[indexDateId];
  if (indexDateType && !indexDateValueInputFieldConfig) {
    throw new Error(`Input field config not found for index date value id: ${indexDateId}`);
  }

  const eventTypeSelectedValue = indexDateValueInputFieldConfig?.options?.find(
    (option) => option.value === indexDateValue
  );

  return (
    <ApiWorkflowDialog
      id="indexDateModal"
      title={
        isEditable
          ? intl.formatMessage({ id: 'projects.cohortBuilderPage.setIndexDateDialog.titleSet' })
          : intl.formatMessage({ id: 'projects.cohortBuilderPage.setIndexDateDialog.titleView' })
      }
      size="medium"
      // isInitialLoading={isLoading}
      // initialLoadingError={conceptGroupsError}
      // onRetryInitialLoadingFetch={refetchConceptGroups}
      isValidForSubmit={!!isSaveAllowed}
      onClose={onClickCancelIndexDate}
      onSubmit={() => onClickModalSetIndexDate(emitResearchAssistantUpdate)}
      // onSubmit={onSubmit}
      // isSubmitting={isSaveStudyArmLoading}
      // submittingError={saveStudyArmError}
    >
      <Box
        sx={{
          marginTop: '24px',
          ...(!isEditable
            ? {
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                height: theme.spacing(20),
                width: '100%',
                backgroundColor: theme.palette.grey[100],
                pl: theme.spacing(6),
                pr: theme.spacing(6),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.spacing(2),
                boxShadow: 'none',
                boxSizing: 'border-box',
              }
            : ''),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '16px' }}>
          <Box>
            <Box sx={{ width: theme.spacing(41) }}>
              <Dropdown
                id={'index-date-type-select'}
                label={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.setIndexDateDialog.typeLabel',
                })}
                width="auto"
                value={indexDateType}
                options={indexDateDropdownOptions}
                onChange={(e) =>
                  onSelectTypeChange(e.target.name, e.target.value as IndexDateTypeLegacy)
                }
                placeholder={intl.formatMessage({
                  id: 'our.cohortBuilder.setIndexDateDialog.typePlaceholder',
                })}
                viewMode={!isEditable}
              ></Dropdown>
            </Box>
          </Box>
          {indexDateType === IndexDateTypeLegacy.EVENT && (
            <Box sx={{ width: theme.spacing(93) }}>
              <ComboBox
                id={`${indexDateId}-index-date-event`}
                label={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.setIndexDateDialog.eventLabel',
                })}
                width={indexDateValueInputFieldConfig.width}
                value={
                  eventTypeSelectedValue
                    ? {
                        value: eventTypeSelectedValue.value || '',
                        displayValue: eventTypeSelectedValue?.displayValue,
                      }
                    : {
                        value: '',
                        displayValue: '',
                      }
                }
                options={indexDateValueInputFieldConfig.options || []}
                onChange={(_, value) => {
                  const _value = value as DropdownListItem;
                  return onEventChange(_value?.value);
                }}
                placeholder={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.setIndexDateDialog.eventPlaceholder',
                })}
                noOptionsText={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.setIndexDateDialog.noOptionsText',
                })}
                viewMode={!isEditable}
              />
            </Box>
          )}
          {indexDateType === IndexDateTypeLegacy.CALENDAR && (
            <Box sx={{ width: theme.spacing(67) }}>
              <DatePicker
                id={`${indexDateId}-index-date-picker`}
                displayLabel={intl.formatMessage({
                  id: 'projects.cohortBuilderPage.setIndexDateDialog.calendarDateLabel',
                })}
                width={indexDateValueInputFieldConfig.width}
                value={indexDateValueAsDate}
                viewMode={!isEditable}
                minDate={dayjs('1900-01-01')}
                maxDate={dayjs('2099-12-31')}
                helperText={datePickerErrorMsg || ''} // Empty string to prevent layout shift
                onChange={(id, value) => {
                  onDatePickerChange(value);
                }}
                onError={(id, error) => {
                  onDatePickerError(error as string);
                }}
              />
            </Box>
          )}
        </Box>
        {indexDateType === IndexDateTypeLegacy.EVENT && (
          <IndexDateRangeSection
            isEditable={!!isEditable}
            indexStartDatePicker={indexStartDatePicker}
            indexEndDatePicker={indexEndDatePicker}
            isDateRangeEnabled={isDateRangeEnabled}
            onClickDateRangeCheckbox={onClickDateRangeCheckbox}
          />
        )}
      </Box>
    </ApiWorkflowDialog>
  );
};

export default IndexDateEditCard;
